import React, { useState } from "react";
import {
  Box,
  Button,
  createStyles,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import _ from "lodash";
import WithLoading from "../../../../utils/WithLoading";
import { CustomTheme } from "../../../../types/customTheme";
import { handleDownloadPdf } from "./convert/handlePdf";
import { handleDownloadExcel } from "./convert/handleExcel";
import LocationDetailsCard from "../../../../components/common/LocationDetailsCard";
import MenuButtonCommon from "../../../../components/buttons/MenuButtonCommon";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Autocomplete } from "@material-ui/lab";
export interface ShiftHeaderProps {
  selectedRoleObj: any;
  days: any;
  startDate: any;
  dummyData: any;
  valueTotal: any;
  value: any;
  selectedDaysList: any;
}

const useStyles = makeStyles((theme: any) =>
  createStyles({
    tableContainer: {
      overflowX: "auto", // Enable horizontal scroll if needed
      backgroundColor: "transparent",
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px", // Adjust spacing here (horizontal vertical)
    },
    dateCell: {
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      padding: "8px",
      borderBottom: "none",
    },
    hourCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default, // Ensure background is solid for fixed column
      zIndex: 1, // Ensure it stays on top of other cells when scrolling
      fontWeight: "bold",
      borderBottom: "none",
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
    hourHeaderCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default,
      zIndex: 3, // Ensure the header stays on top
      fontWeight: "bold",
      borderBottom: "none",
    },
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

/**
 * NormalSalesTable Component:
 *
 * This component handles the header section of the Shift component, including
 * sorting controls, location selection, and shift generation. It displays shift
 * information in a table-like format with sortable columns and provides options
 * for selecting shifts, generating reports, and handling user interactions.
 */
const NormalSalesTable: React.FunctionComponent<ShiftHeaderProps> = ({
  selectedRoleObj,
  days,
  startDate,
  dummyData,
  valueTotal,
  value,
  selectedDaysList,
}) => {
  const theme: CustomTheme = useTheme();

  const addDaysToDate = (dateStr: any, daysToAdd: any) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + daysToAdd);

    // Format to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getWeekdayAbbreviation = (dateStr: any) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { weekday: "long" }); // e.g., "Thu"
  };

  const formatDateWithWeekday = (dateStr: string): any => {
    const date = new Date(dateStr);

    const weekday = date.toLocaleDateString("en-GB", { weekday: "short" });

    return (
      <div>
        <div>{weekday}</div>
        <div>{dateStr}</div>
      </div>
    );
  };

  const getColor = (value: number, list: any) => {
    const startColor = { r: 222, g: 255, b: 235 }; // #DEFFEB
    const endColor = { r: 2, g: 105, b: 87 }; // #026957
    const index = list.indexOf(value);

    const maxIndex = list.length - 1;
    const ratio = maxIndex === 0 ? 0 : index / maxIndex;
    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);

    const color =
      value === 0
        ? theme.palette.background.default === "#FFFFFF"
          ? theme.palette.background.entity_border_highlight_background
          : theme.palette.background.entity_highlight_background
        : `rgb(${r}, ${g}, ${b})`;
    return color;
  };

  /**
   * Checks if a color is dark based on luminance.
   * @param hex - Hex color string (e.g., '#FFFFFF' or '#026957')
   * @returns true if dark, false if light
   */
  const isDarkColor = (color: string): boolean => {
    let r = 0,
      g = 0,
      b = 0;

    if (color.startsWith("rgb")) {
      const match = color.match(/rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/);
      if (match) {
        r = parseInt(match[1], 10);
        g = parseInt(match[2], 10);
        b = parseInt(match[3], 10);
      } else {
        return false;
      }
    } else if (color.startsWith("#")) {
      const hex = color.replace("#", "");
      if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else {
        return false;
      }
    } else {
      return false;
    }

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    return luminance < 128;
  };

  const handleMarginLeft = (long: any) => {
    if (long === "Friday") {
      return "20px";
    } else if (long === "Saturday" && !selectedDaysList.includes("friday")) {
      return "20px";
    } else if (
      long === "Sunday" &&
      !selectedDaysList.includes("friday") &&
      !selectedDaysList.includes("saturday")
    ) {
      return "20px";
    } else {
      return "0px";
    }
  };

  const handleMarginRight = (long: any) => {
    if (long === "Sunday") {
      return "20px";
    } else if (long === "Saturday" && !selectedDaysList.includes("sunday")) {
      return "20px";
    } else if (
      long === "Friday" &&
      !selectedDaysList.includes("sunday") &&
      !selectedDaysList.includes("saturday")
    ) {
      return "20px";
    } else {
      return "0px";
    }
  };
  const classes = useStyles();

  return (
    <Table className={classes.table} size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell className={classes.hourHeaderCell}>Hour</TableCell>
          {days.map((day: any, i: any) => {
             const long = getWeekdayAbbreviation(day);
              const marginLeft = handleMarginLeft(long);
              const marginRight = handleMarginRight(long);
            return (
              <TableCell
                key={i}
                align="center"
                className={classes.dateCell}
                style={{
                  fontWeight: "bold",
                  borderBottom: "none",
                }}
              >
                <div
                  style={{
                    marginLeft: marginLeft,
                    marginRight: marginRight,
                  }}
                >
                  {formatDateWithWeekday(day)}
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {dummyData.map((row: any, i: any) => (
          <TableRow key={i}>
            <TableCell
              className={classes.hourCell}
              style={{ borderBottom: "none" }}
            >
              {row.hour}
            </TableCell>
            {row.data.map((cell: any, j: any) => {
              const long = getWeekdayAbbreviation(cell.date);
              const marginLeft = handleMarginLeft(long);
              const marginRight = handleMarginRight(long);
              // Calculate the background color based on the count
              const bgColor = getColor(
                selectedRoleObj.id === "total"
                  ? parseFloat(cell.value)
                  : cell.count,
                selectedRoleObj.id === "total" ? valueTotal : value,
              );
              return (
                <TableCell
                  key={j}
                  align="center"
                  style={{
                    borderBottom: "none",
                    width: `${100 / days.length}%`,
                    padding: "0px",
                  }}
                >
                  <div
                    style={{
                      marginLeft: marginLeft,
                      marginRight: marginRight,
                      backgroundColor: bgColor,
                      borderRadius: "10px",
                      height: selectedRoleObj.id === "all" ? "42px" : "32px",
                    }}
                  >
                    {selectedRoleObj.id === "all" ? (
                      <div>
                        <Typography
                          variant="body2"
                          style={{
                            color: isDarkColor(bgColor) ? "white" : "black",
                            fontWeight: "bold",
                          }}
                        >
                          {cell.count}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{
                            color: isDarkColor(bgColor) ? "white" : "black",
                          }}
                        >
                          {cell.value === 0
                            ? "-"
                            : parseFloat(cell.value).toFixed(2)}
                        </Typography>
                      </div>
                    ) : (
                      <Typography
                        style={{
                          color: isDarkColor(bgColor) ? "white" : "black",
                          paddingTop: "4px",
                        }}
                      >
                        {selectedRoleObj.id === "count"
                          ? cell.count
                          : cell.value === 0
                          ? "-"
                          : parseFloat(cell.value).toFixed(2)}
                      </Typography>
                    )}
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default NormalSalesTable;
